<template>
	<v-menu
		ref="menu"
		v-model="menu"
		:model-value="defaultDate"
		:close-on-content-click="true"
		:offset="40"
		transition="scale-transition"
		min-width="290px"
	>
		<template #activator="{ props }">
			<v-text-field
				:id="name + '_formatted'"
				v-model="computedMenuState"
				:label="label"
				:name="name + '_formatted'"
				:error-messages="concatenatedErrors"
				:variant="outline ? 'outlined' : ''"
				:disabled="disabled"
				:rules="rules"
				readonly
				v-bind="props"
				@blur="dateValue = parseDate(dateFormatted)"
			/>
		</template>
		<v-date-picker
			v-model="computedMenuState"
			no-title
			scrollable
			type="month"
			@input="menu = false"
		/>
	</v-menu>
</template>

<script>
export default {
	name: 'MonthPicker',
	props: {
		label: {
			type: String,
			default: '',
		},
		name: {
			type: String,
			required: true,
		},
		value: {
			type: String,
			default: '',
		},
		outline: {
			type: Boolean,
			default: false,
		},
		errorMessages: {
			type: Array,
			default: () => [],
		},
		defaultDate: {
			type: String,
			default: () => '',
		},
		disabled: {
			type: Boolean,
			default: () => false,
		},
		rules: {
			type: Array,
			default: () => [],
		},
	},

	emits: ['input', 'change',],

	data () {
		return {
			menu: false,
			dateValue: null,

			dateFormatted: null,
			errors: [],
		};
	},

	computed: {
		/**
		 * Concatenation of all error messages
		 *
		 * @returns {Array}
		 */
		concatenatedErrors: function () {
			return this.errors.concat(this.errorMessages === null ? [] : this.errorMessages);
		},

		computedMenuState: {
			/**
			 * @returns {string} date value
			 */
			get () {
				return (this.dateValue) ? this.dateValue : this.defaultDate;
			},

			/**
			 * @param {string} value
			 * @returns {void}
			 */
			set (value) {
				this.dateValue = value;
			},
		},
	},

	watch: {
		dateValue () {
			this.errors = [];
			this.dateFormatted = this.formatDate(this.dateValue);
			this.$emit('input', this.dateValue);
			this.$emit('change', this.dateValue);
		},
	},

	mounted () {
		this.dateValue = this.value;
		this.dateFormatted = this.formatDate(this.dateValue);
	},

	methods: {
		/**
		 * Converts a ISO 8601 compliant date string into mm-yyyy to comply with netherlands standard for viewing
		 *
		 * @param {string} date - a date string we're going to format
		 * @returns {string|null} - either the formatted string or nothing indicating something went wrong
		 */
		formatDate (date) {
			if (!date) {
				return null;
			}
			const [year, month, ] = date.split('-');

			if (!this.validateDate(year, month)) {
				return null;
			}

			return `${month}-${year}`;
		},
		/**
		 * Reads a mm-yyyy format date string and converts it to ISO 8601 format for over
		 * the line transmission
		 *
		 * @param {string} date a date string we're going to format
		 * @returns {string|null} - either the formatted string or nothing indicating something went wrong
		 */
		parseDate (date) {
			if (!date) {
				return null;
			}
			const [month, year, ] = date.split('-');

			if (!this.validateDate(year, month)) {
				return null;
			}

			return `${year}-${month.padStart(2, '0')}`;
		},
		/**
		 * Validates a date is in the correct range and has the correct number of digits
		 *
		 * @param {string} year - the year
		 * @param {string} month - the year
		 * @returns {boolean} - whether or not the date was valid
		 */
		validateDate (year, month) {
			this.errors = [];
			// Validate the date before trying to parse it so we don't get errors
			if (
				!(month > 0) || !(month < 13) ||
				this.digitsCount(parseInt(year)) !== 4
			) {
				this.errors = [this.$gettext('Invalid date'), ];
				return false;
			}
			this.errors = [];
			return true;
		},

		/**
		 * Validates a date is in the correct range and has the correct number of digits
		 *
		 * @param {number} rawValue - the integer representing the year
		 * @returns {number} - the count of digits in the number
		 */
		digitsCount (rawValue) {
			let count = 0;
			let value = rawValue;

			if (value >= 1) {
				++count;
			}

			while (value / 10 >= 1) {
				value /= 10;
				++count;
			}

			return count;
		},
	}
};
</script>
